import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ToastProvider/ToastProvider';
import { ValidationResult } from '@/features/scheduler/utils/mediaValidation';

const CLEAN_SUCCESS_TOAST_DURATION = 2;
type Translate = ReturnType<typeof useTranslation>['t'];
type ShowToast = ReturnType<typeof useToast>['showToast'];

interface ValidationFeedbackProps {
  validationResult: ValidationResult | null;
  fileName?: string;
}

const showSuccessToast = (
  filePrefix: string,
  showToast: ShowToast,
  t: Translate,
) => {
  showToast({
    variant: 'success',
    title: t('validation.fileValid'),
    text: `${filePrefix}${t('validation.fileValidDescription')}`,
    icon: 'check-circle',
    duration: CLEAN_SUCCESS_TOAST_DURATION,
  });
};

const showErrorToast = (
  validationResult: ValidationResult,
  fileName: string | undefined,
  showToast: ShowToast,
  t: Translate,
) => {
  const errorText = validationResult.errors.join('; ');
  const isFileError = Boolean(fileName) || validationResult.errors.includes(t('validation.fileErrorDescription'));
  const title = isFileError ? t('validation.fileError') : t('validation.captionError');
  let text = `${t('validation.captionErrorDescription')} ${errorText}`;
  if (isFileError) {
    if (fileName) {
      text = `${t('validation.fileErrorDescription')} ${t('validation.details')} ${errorText}`;
    } else {
      text = errorText;
    }
  }

  showToast({ variant: 'error', title, text, icon: 'error' });
};

export const ValidationFeedback: React.FC<ValidationFeedbackProps> = ({
  validationResult,
  fileName
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!validationResult) {
      return;
    }

    const filePrefix = fileName ? `${fileName}: ` : '';

    if (validationResult.isValid) {
      showSuccessToast(filePrefix, showToast, t);
      return;
    }

    showErrorToast(validationResult, fileName, showToast, t);
  }, [validationResult, fileName, showToast, t]);
  if (!validationResult?.warnings.length) return null;

  return (
    <output className="validation-feedback_warnings">
      {validationResult.warnings.join('; ')}
    </output>
  );
};